import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../../shared/utils/localization';
import type { Consultation } from '../types/Consultation';
import styles from './RelatedConsultationsPanel.module.css';

export type RelatedConsultationsPanelProps = {
  consultations: Consultation[];
  emptyLabel?: string;
  returnTo?: string;
};

const getConsultationLabel = (consultation: Consultation): string => {
  return consultation.appointmentTypeName
    || consultation.templateName
    || consultation.assetName
    || consultation.customerContactName
    || consultation.contactName
    || consultation.calendarEventTitle
    || consultation.summary
    || consultation.id;
};

export const RelatedConsultationsPanel: React.FC<RelatedConsultationsPanelProps> = ({
  consultations,
  emptyLabel,
  returnTo,
}) => {
  const { t } = useTranslation(['consultations', 'assets', 'common']);
  const navigate = useNavigate();

  if (consultations.length === 0) {
    return (
      <div className={styles.emptyState}>
        {emptyLabel || t('noRelatedConsultations', { defaultValue: 'No consultations found.' })}
      </div>
    );
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('updated', { ns: 'common', defaultValue: 'Updated' })}</th>
            <th>{t('consultation', { defaultValue: 'Consultation' })}</th>
            <th>{t('status', { defaultValue: 'Status' })}</th>
            <th>{t('contact', { defaultValue: 'Contact' })}</th>
            <th>{t('asset', { ns: 'assets', defaultValue: 'Asset' })}</th>
          </tr>
        </thead>
        <tbody>
          {consultations.map((consultation) => (
            <tr
              key={consultation.id}
              onClick={() => navigate(`/consultations/${consultation.id}`, returnTo ? { state: { returnTo } } : undefined)}
            >
              <td>{formatDateTime(consultation.updatedAt)}</td>
              <td>{getConsultationLabel(consultation)}</td>
              <td>{consultation.status || t('draft', { defaultValue: 'Draft' })}</td>
              <td>{consultation.customerContactName || consultation.contactName || '-'}</td>
              <td>{consultation.assetName || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RelatedConsultationsPanel;
